import _ from 'lodash'
import moment from 'moment'

import { dateToString } from 'modules/core/lib/dates'

import Compensation from 'modules/contracts/store/models/Compensation'
import { DraftCompensation, NO_POLICY_ID } from 'modules/employees/pages/BulkFixContractsPage/types'

import {
  type CompensationUpdateAction,
  setFirstPaymentAction,
  setLegalEntityAction,
  setPolicyAction,
} from './actions'
import {
  CompensationWizardData,
  CompensationWizardMeta,
  CompletedCompensationWizardData,
} from './types'

// TODO: improve types within Compensation
const COMPENSATION_RECURRENCE_MONTHLY = 'monthly'

export const wizardReducer: React.Reducer<CompensationWizardData, CompensationUpdateAction> = (
  state,
  action
) => {
  switch (action.type) {
    case 'setLegalEntity': {
      // Same LE, no change
      if (state.legalEntityId === action.payload) return state

      const newState: CompensationWizardData = {
        ...state,
        legalEntityId: action.payload,
      }

      const { affectedEmployeeIds, unaffectedEmployeeIds } = determineAffectedEmployees(newState)

      // eslint-disable-next-line no-type-assertion/no-type-assertion -- currency is required to be allowed
      const unit = state.allowedLegalEntities.find((le) => le.id === action.payload)!.currency!

      return {
        ...newState,
        affectedEmployeeIds,
        unaffectedEmployeeIds,
        // Reset dependent form data if the legal entity changes
        unit,
        contractsTaxonomyId: undefined,
        recurrenceCount: 1,
        recurrence: COMPENSATION_RECURRENCE_MONTHLY,
        amount: undefined,
      }
    }
    case 'setPolicy': {
      const newState: CompensationWizardData = {
        ...state,
        policyId: action.payload,
      }

      const { affectedEmployeeIds, unaffectedEmployeeIds } = determineAffectedEmployees(newState)

      newState.syncWithSupplements = newState.policyId !== NO_POLICY_ID

      return {
        ...newState,
        affectedEmployeeIds,
        unaffectedEmployeeIds,
      }
    }
    case 'setTaxonomy': {
      const newState: CompensationWizardData = {
        ...state,
        contractsTaxonomyId: action.payload?.id ?? undefined,
        taxonomyName: action.payload?.name ?? undefined,
      }

      return newState
    }
    case 'setRateType': {
      if (action.payload === state.compensationType) return state

      const newState: CompensationWizardData = {
        ...state,
        compensationType: action.payload ?? undefined,
        // unset calculation if not applicable
        calculation: undefined,
      }

      if (!action.payload || action.payload === Compensation.types.undefined) {
        newState.amount = undefined
      } else if (action.payload === Compensation.types.per_worked_day) {
        // default to current period
        newState.calculation = 'current_period'
      }
      return newState
    }
    case 'setDescription': {
      const newState: CompensationWizardData = {
        ...state,
        description: action.payload,
      }
      return newState
    }
    case 'setAmount': {
      const newState: CompensationWizardData = {
        ...state,
        amount: action.payload ?? undefined,
      }
      return newState
    }
    case 'setAmountType': {
      const newState: CompensationWizardData = {
        ...state,
        unit: action.payload,
      }
      return newState
    }
    case 'setSyncWithPayroll': {
      const newState: CompensationWizardData = {
        ...state,
        syncWithSupplements: action.payload,
      }
      return newState
    }
    case 'setRecurrenceCount': {
      const newState: CompensationWizardData = {
        ...state,
        recurrenceCount: action.payload,
      }
      return newState
    }
    case 'setStartsOn': {
      const newState: CompensationWizardData = {
        ...state,
        startsOn: action.payload,
      }
      return newState
    }
    case 'setRecurrence': {
      const newState: CompensationWizardData = {
        ...state,
        recurrence: action.payload,
      }
      return newState
    }
    case 'setFirstPayment': {
      const newState: CompensationWizardData = {
        ...state,
        firstPaymentOn: action.payload,
      }
      return newState
    }
    case 'setPerWorkedDayCalculation': {
      const newState: CompensationWizardData = {
        ...state,
        calculation: action.payload,
      }
      return newState
    }
    case 'setTimeCondition': {
      const newState: CompensationWizardData = {
        ...state,
        timeCondition: action.payload ?? undefined,
      }
      return newState
    }
    case 'setMinimumAmountOfHours': {
      const newState: CompensationWizardData = {
        ...state,
        minimumAmountOfHours: action.payload ?? undefined,
      }
      return newState
    }
    default:
      const _exhaustiveCheck: never = action
      throw new Error(`Unallowed action: ${_exhaustiveCheck}`)
  }
}

export function computeInitialState(
  meta: CompensationWizardMeta,
  initialData: DraftCompensation
): CompensationWizardData {
  const { allowedLegalEntities, allowedPayrollPolicies, targetEmployees } = meta

  let draft: CompensationWizardData = {
    targetEmployees,
    allowedLegalEntities,
    allowedPayrollPolicies,

    affectedEmployeeIds: [],
    unaffectedEmployeeIds: Object.values(targetEmployees).map((e) => e.id),
    recurrence: '1',

    ...initialData,
  }

  draft = setupLegalEntity(draft)
  draft = setupPayrollPolicies(draft)
  draft = setupFirstPayment(draft)

  return draft
}

function setupLegalEntity(draft: CompensationWizardData) {
  const { allowedLegalEntities } = draft

  // Default to legal entity and policy if only one exists, respectively
  if (allowedLegalEntities.length === 1 && allowedLegalEntities[0]) {
    draft = wizardReducer(draft, setLegalEntityAction(allowedLegalEntities[0].id))
  }

  return draft
}

function setupPayrollPolicies(draft: CompensationWizardData) {
  const { allowedPayrollPolicies, targetEmployees } = draft

  // Default to payroll policy if only one exists, or all selected employees
  // are attached to the same policy
  if (allowedPayrollPolicies.length === 1 && allowedPayrollPolicies[0]) {
    draft = wizardReducer(draft, setPolicyAction(allowedPayrollPolicies[0].id))
  } else {
    const employeePolicyIds = _.uniq(Object.values(targetEmployees).map((e) => e.payrollPolicyId))
    if (employeePolicyIds.length === 1) {
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      draft = wizardReducer(draft, setPolicyAction(employeePolicyIds[0]!))
    }
  }
  return draft
}

function setupFirstPayment(draft: CompensationWizardData) {
  const { firstPaymentOn } = draft

  if (!firstPaymentOn) {
    draft = wizardReducer(
      draft,
      setFirstPaymentAction(dateToString(moment().startOf('month'), 'backend'))
    )
  }

  return draft
}

function employeeIsAffected(
  employeeId: string,
  { legalEntityId, policyId, targetEmployees }: CompensationWizardData
) {
  if (!legalEntityId || !policyId) return false
  const employeeProfile = targetEmployees[employeeId]
  if (!employeeProfile) return false

  return (
    employeeProfile.legalEntityId === legalEntityId && employeeProfile.payrollPolicyId === policyId
  )
}

function determineAffectedEmployees(
  state: CompensationWizardData
): Pick<CompensationWizardData, 'affectedEmployeeIds' | 'unaffectedEmployeeIds'> {
  const affectedEmployeeIds: string[] = []
  const unaffectedEmployeeIds: string[] = []

  Object.values(state.targetEmployees).forEach((employeeProfile) => {
    if (employeeIsAffected(employeeProfile.id, state)) {
      affectedEmployeeIds.push(employeeProfile.id)
    } else {
      unaffectedEmployeeIds.push(employeeProfile.id)
    }
  })

  return { affectedEmployeeIds, unaffectedEmployeeIds }
}

export function isValidTemplate(
  data: CompensationWizardData
): data is CompletedCompensationWizardData {
  // TODO: Improve validation logic using a proper schema
  const {
    contractsTaxonomyId,
    compensationType,
    amount,
    unit,
    syncWithSupplements,
    calculation,
    timeCondition,
    minimumAmountOfHours,
  } = data

  const requiredFields: (string | number | undefined)[] = [
    contractsTaxonomyId,
    compensationType,
    unit,
  ]
  if (compensationType === 'per_worked_day') {
    requiredFields.push(calculation)
    requiredFields.push(timeCondition)
  }
  if (timeCondition === 'custom') {
    requiredFields.push(minimumAmountOfHours)
  }

  if (compensationType !== 'undefined') {
    requiredFields.push(amount)
  }

  if (syncWithSupplements) {
    requiredFields.push(data.recurrenceCount, data.startsOn)
  } else {
    requiredFields.push(data.recurrence, data.firstPaymentOn)
  }

  const isValid =
    data.affectedEmployeeIds.length > 0 && requiredFields.every((field) => field !== undefined)
  return isValid
}
